import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { HostRoom } from './host-room';
import { PlayerRoom } from './player-room';
import { UserResult } from '@shared/api-dto';
import { MoveType } from '@shared/constants';
import { useRoomData } from '../../api/hooks';
import { Header } from '../header';
import { SplitWipe, isSplitEntrance } from '../split-wipe';

import './room.css';

type GameRoomProps = {
  user: UserResult;
}
export const Room = ({ user }: GameRoomProps) => {
  const { code } = useParams();
  const location = useLocation();
  const [isLoading, error, roomData] = useRoomData(code);
  const isHost = user.guid === roomData?.host.guid;
  // Arriving from "Start a new game": the split closed over the landing
  // screen, so open it here to reveal the room. `entering` stays on for the
  // life of the component so the lobby's staggered entrance can finish.
  const [entering] = useState(() => isSplitEntrance(location.state));
  const [wipeOpen, setWipeOpen] = useState(entering);

  useEffect(() => {
    if (roomData?.nextRoomCode && !isHost) {
      window.location.href = `/room/${roomData.nextRoomCode}?skip_name=1`;
    }
  }, [roomData?.nextRoomCode, isHost]);

  // Players drawing on their phones need every pixel, so drop the header for them.
  const hideHeader = Boolean(
    roomData && !isHost && roomData.isActive &&
    roomData.currentMove?.type === MoveType.DRAWN_TOGETHER
  );

  const containerClass = [
    'roomContainer',
    hideHeader ? 'noHeader' : '',
    entering ? 'entering' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass}>
      <Header roomCode={roomData?.code} />
      <div className='headerAccent'></div>
      <div className='roomContent'>
        {wipeOpen && (
          <SplitWipe direction='opening' onDone={() => setWipeOpen(false)} />
        )}
        {error && (<div className='error'><span>{error}</span></div>)}
        {isLoading && (<div className='loadingSpinner'></div>)}
        {roomData && isHost && (
          <HostRoom roomData={roomData} />
        )}
        {roomData && !isHost && (
          <PlayerRoom roomData={roomData} user={user} />
        )}
      </div>
    </div>
  );
};
